import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { db } from '@siaedts/shared/lib/supabase';

const values = [
  {
    icon: (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
      </svg>
    ),
    title: 'Integrity First',
    description: 'We build lasting relationships through honesty, transparency, and ethical business practices in every interaction.',
  },
  {
    icon: (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
    ),
    title: 'Customer Success',
    description: 'Your productivity is our priority. We measure our success by the success of the farmers we serve.',
  },
  {
    icon: (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
      </svg>
    ),
    title: 'Innovation',
    description: 'We continuously adopt cutting-edge technology to bring you the most efficient, sustainable farming solutions.',
  },
  {
    icon: (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
      </svg>
    ),
    title: 'Community',
    description: 'We are deeply rooted in the agricultural community, supporting local farms, events, and future farmers.',
  },
];

const milestones = [
  { year: '2009', title: 'Company Founded', description: 'Started as a small family dealership with 3 employees' },
  { year: '2012', title: 'John Deere Partnership', description: 'Became authorized John Deere dealer for the region' },
  { year: '2015', title: 'Service Expansion', description: 'Opened full-service workshop with 12 service bays' },
  { year: '2018', title: 'Precision Ag Division', description: 'Launched dedicated precision agriculture technology team' },
  { year: '2021', title: 'New Facility', description: 'Moved to 50,000 sq ft state-of-the-art showroom and service center' },
  { year: '2024', title: 'Valley Irrigation Added', description: 'Expanded portfolio with industry-leading irrigation solutions' },
  { year: '2026', title: '15 Year Anniversary', description: 'Celebrating 15 years serving the agricultural community' },
];

export function About() {
  const { t } = useTranslation();
  const [aboutContent, setAboutContent] = useState<{
    hero_description?: string;
    company_paragraphs?: string[];
    values?: Array<{ title: string; description: string }>;
    milestones?: Array<{ year: string; title: string; description: string }>;
    story_image_url?: string | null;
    story_video_url?: string | null;
    values_subtitle?: string;
    milestones_subtitle?: string;
    team_subtitle?: string;
    ecosystem_subtitle?: string;
    leadership_title?: string;
    ecosystem_title?: string;
    partners_title?: string;
    certifications_title?: string;
    certifications_subtitle?: string;
    certifications?: Array<{ title: string; subtitle: string; icon: string }>;
  } | null>(null);
  const [teamMembers, setTeamMembers] = useState<Array<{ id: string; name: string; role: string; bio?: string | null; image_url?: string | null }>>([]);
  const [partners, setPartners] = useState<Array<{
    id: string;
    name: string;
    description: string | null;
    logo_url: string | null;
    website_url: string | null;
    type: 'international' | 'national';
    is_active: boolean;
  }>>([]);

  useEffect(() => {
    let mounted = true;

    async function loadPartners() {
      try {
        const [rows, team] = await Promise.all([db.partners.getAll({ is_active: true }), db.team.getAll({ is_active: true })]);
        if (!mounted) return;
        setPartners((rows ?? []) as Array<{
          id: string;
          name: string;
          description: string | null;
          logo_url: string | null;
          website_url: string | null;
          type: 'international' | 'national';
          is_active: boolean;
        }>);
        setTeamMembers((team ?? []) as Array<{ id: string; name: string; role: string; bio?: string | null; image_url?: string | null }>);
      } catch (error) {
        console.error('Failed to load partners:', error);
        if (mounted) setPartners([]);
        if (mounted) setTeamMembers([]);
      }
    }

    void loadPartners();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    void db.about.get().then((content) => {
      if (mounted && content) setAboutContent(content);
    }).catch(() => undefined);
    return () => { mounted = false; };
  }, []);

  const internationalPartners = partners.filter((partner) => partner.type === 'international');
  const nationalPartners = partners.filter((partner) => partner.type === 'national');
  const displayedValues = (aboutContent?.values?.length ? aboutContent.values : values)
    .filter((value) => value.title.trim().length > 0 || value.description.trim().length > 0)
    .map((value, index) => ({ ...value, icon: values[index % values.length].icon }));
  const displayedMilestones = (aboutContent?.milestones?.length ? aboutContent.milestones : milestones)
    .filter((milestone) => milestone.year.trim().length > 0 || milestone.title.trim().length > 0 || milestone.description.trim().length > 0);
  return (
    <>
      {/* Page Header */}
      <section className="bg-green-700 text-white py-16 sm:py-24">
        <div className="container-main">
          <div className="max-w-3xl">
            <h1 className="text-4xl sm:text-5xl font-bold mb-4">About SIAEDTS-SARL</h1>
            <p className="text-green-100 text-lg">
              {aboutContent?.hero_description ?? "A modern, professional, and reliable agro-livestock and veterinary company based in Macenta, Guinea's Forest Region."}
            </p>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="section bg-white">
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="section-title">Our company</h2>
              <p className="text-gray-600 text-lg mb-6">
                {aboutContent?.company_paragraphs?.[0] ?? "SIAEDTS-SARL is an industrial agro-livestock company headquartered in Macenta Centre, in the Bamala district of Guinea's Forest Region, N'zérékoré."}
              </p>
              <p className="text-gray-600 mb-6">
                {aboutContent?.company_paragraphs?.[1] ?? 'Our work covers industry in all its forms, the management and study of cultivable land, and applied research in livestock.'}
              </p>
              <p className="text-gray-600">
                {aboutContent?.company_paragraphs?.[2] ?? 'We also import and export veterinary products and agricultural inputs in Guinea, while investing in training, health, and safety. Our ambition is to become a reference in the agro-livestock and veterinary sector.'}
              </p>
            </div>
            <div className="relative">
              <div className="aspect-video bg-gray-100 rounded-2xl overflow-hidden">
                {aboutContent?.story_image_url ? <img src={aboutContent.story_image_url} alt="SIAEDTS-SARL story" className="absolute inset-0 h-full w-full object-cover" /> : <div className="absolute inset-0 flex items-center justify-center text-gray-400">
                  <svg className="w-24 h-24" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z" />
                  </svg>
                </div>}
                <div className="absolute bottom-6 left-6 right-6 bg-white/95 backdrop-blur-sm rounded-xl p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Watch Our Story</h3>
                  <p className="text-gray-600 mb-4">Learn more about our journey and commitment to agriculture</p>
                  <button className="btn-primary inline-flex items-center space-x-2">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path d="M8 5v14l11-7z" />
                    </svg>
                    <span>Play Video</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="section bg-gray-50">
        <div className="container-main">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="section-title">Our Core Values</h2>
            <p className="section-subtitle">{aboutContent?.values_subtitle ?? 'The principles that guide everything we do'}</p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {displayedValues.map((value, index) => (
              <div key={index} className="card p-6 text-center hover:shadow-xl transition-shadow duration-300">
                <div className="w-16 h-16 bg-green-100 rounded-xl flex items-center justify-center text-green-700 mx-auto mb-4">
                  {value.icon}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{value.title}</h3>
                <p className="text-gray-600">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="section bg-white">
        <div className="container-main">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="section-title">Our Journey</h2>
            <p className="section-subtitle">{aboutContent?.milestones_subtitle ?? 'Key milestones in our 15+ year history'}</p>
          </div>
          
          <div className="relative max-w-3xl mx-auto">
            <div className="absolute left-1/2 top-0 bottom-0 w-0.5 bg-green-200 transform -translate-x-1/2" />
            
            <div className="space-y-12">
              {displayedMilestones.map((milestone, index) => (
                <div key={milestone.year} className="relative flex items-center">
                  <div className={`w-1/2 pr-8 ${index % 2 === 0 ? 'text-right' : 'pl-8 ml-auto'}`}>
                    <div className={`card p-6 ${index % 2 === 0 ? 'mr-8' : 'ml-8'}`}>
                      <div className="flex items-center space-x-2 mb-2">
                        <span className="badge badge-green">{milestone.year}</span>
                        <h3 className="text-lg font-semibold text-gray-900">{milestone.title}</h3>
                      </div>
                      <p className="text-gray-600">{milestone.description}</p>
                    </div>
                  </div>
                  <div className="absolute left-1/2 transform -translate-x-1/2 w-4 h-4 bg-green-600 rounded-full border-4 border-white z-10" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="section bg-gray-50">
        <div className="container-main">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="section-title">{aboutContent?.leadership_title ?? 'Meet Our Leadership'}</h2>
            <p className="section-subtitle">{aboutContent?.team_subtitle ?? 'Experienced professionals dedicated to your success'}</p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {teamMembers.map((member) => (
              <div key={member.id} className="card overflow-hidden">
                <div className="aspect-square bg-gray-100 relative overflow-hidden">
                  {member.image_url ? <img src={member.image_url} alt={member.name} className="absolute inset-0 h-full w-full object-cover" /> : <div className="absolute inset-0 flex items-center justify-center text-gray-400">
                    <svg className="w-16 h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </div>}
                </div>
                <div className="p-6 text-center">
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">{member.name}</h3>
                  <p className="text-green-700 font-medium text-sm mb-3">{member.role}</p>
                  <p className="text-gray-600 text-sm">{member.bio ?? ''}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Partners */}
      <section className="section bg-white">
        <div className="container-main">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="section-title">{aboutContent?.ecosystem_title ?? 'Our products & ecosystem'}</h2>
            <p className="section-subtitle">{aboutContent?.ecosystem_subtitle ?? 'From poultry and veterinary products to fresh produce, aquaculture, and trusted national and international partners.'}</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-14">
            {[
              ['Poultry & livestock', 'Table eggs, broiler chicks, day-old layer chicks, poultry feed concentrates, cereals, and animal products.'],
              ['Veterinary products', 'Antibiotics, anti-inflammatory products, vitamins, antiparasitics, and other veterinary solutions.'],
              ['Fresh produce & aquaculture', 'Fresh fruits and vegetables, plantain, banana, lemon, melon, cucumber, lettuce, cabbage, green beans, tomato, eggplant, fish, and live snails.'],
              ['Essential oils & local products', 'Shea butter, kobidoutrou, zylopia, pepper, and other locally rooted products.'],
              ['Agricultural inputs', 'Inputs and practical support for cultivation, livestock, health, and safety across Guinea.'],
              ['Research & land studies', 'Management and study of cultivable land with applied livestock research.'],
            ].map(([title, description]) => <div key={title} className="card p-6"><h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3><p className="text-gray-600 text-sm leading-relaxed">{description}</p></div>)}
          </div>
          <h3 className="text-2xl font-bold text-gray-900 mb-6">{aboutContent?.partners_title ?? t('about.partnersTitle')}</h3>
          <div className="grid md:grid-cols-2 gap-6 mb-10"><div className="card p-6"><h4 className="text-lg font-semibold text-gray-900 mb-2">{t('about.operationalPartners.sahel.name')}</h4><p className="text-gray-600 text-sm leading-relaxed">{t('about.operationalPartners.sahel.description')}</p></div><div className="card p-6"><h4 className="text-lg font-semibold text-gray-900 mb-2">{t('about.operationalPartners.sar.name')}</h4><p className="text-gray-600 text-sm leading-relaxed">{t('about.operationalPartners.sar.description')}</p></div></div>
          <h3 className="text-2xl font-bold text-gray-900 mb-6">International partners</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-10">
            {(internationalPartners.length > 0
              ? internationalPartners.map((partner) => partner.name)
              : ['Vitalac SA, France', 'Medivet SA, Tunisia', 'Biotest Laboratory, Ukraine', 'FAO', 'OMSA', 'WHO']
            ).map((partner) => (
              <div key={partner} className="rounded-lg border border-green-100 bg-green-50 p-4 text-center text-sm font-semibold text-green-800">
                {partner}
              </div>
            ))}
          </div>
          <h3 className="text-2xl font-bold text-gray-900 mb-6">National partners</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {(nationalPartners.length > 0
              ? nationalPartners.map((partner) => partner.name)
              : ['Ministry of Health', 'Ministry of Livestock', 'Ministry of Agriculture & Environment', 'ANBG · Bagbema Ecosystem · AMBG']
            ).map((partner) => (
              <div key={partner} className="rounded-lg border border-gray-200 p-4 text-center text-sm font-semibold text-gray-700">
                {partner}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="section bg-green-700">
        <div className="container-main">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">{aboutContent?.certifications_title ?? 'Certifications & Recognition'}</h2>
            <p className="text-green-100 text-lg">{aboutContent?.certifications_subtitle ?? 'Industry-recognized excellence in service and sales'}</p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {(aboutContent?.certifications?.length ? aboutContent.certifications : [
              { title: 'John Deere', subtitle: 'Platinum Dealer', icon: '🏆' },
              { title: 'Case IH', subtitle: 'Gold Level Dealer', icon: '🥇' },
              { title: 'Valley', subtitle: 'Certified Installer', icon: '💧' },
              { title: 'Kubota', subtitle: 'Elite Dealer', icon: '⭐' },
            ]).map((cert, index) => (
              <div key={index} className="card bg-green-800 p-6 text-center">
                <div className="text-4xl mb-3">{cert.icon}</div>
                <h3 className="text-xl font-bold text-white mb-1">{cert.title}</h3>
                <p className="text-green-200">{cert.subtitle}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section bg-gray-900">
        <div className="container-main text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Join Our Growing Family</h2>
          <p className="text-gray-300 text-lg mb-8 max-w-2xl mx-auto">
            Whether you're looking for equipment, service, parts, or a career in agriculture, 
            we'd love to hear from you.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/contact" className="btn-primary text-lg px-8 py-4">
              Get in Touch
            </Link>
            <Link to="/careers" className="btn border-2 border-gray-600 text-white hover:bg-gray-800 text-lg px-8 py-4">
              View Careers
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}